import { useState, useEffect, useRef } from 'react';
import { ApiGet } from './Function';
import './ScrollBar.css';
import { ReactComponent as TextSize } from "./Image/text-size.svg";
import { titleColor, backgroundMainColor, backgroundSubColor, gridCellBackgroundColor, gridCellFontColor } from './Color';

function PaginationGrid({ apiUrl, mainHeight = 0, rowSpanColumnArray = [], notNumberColumnArray = [], longText = false, inquiryCondition = false, setInquiryFunction, ChangeMonitoring, inquiryInterval }) { 
  const [fontSize, setFontSize] = useState(JSON.parse(localStorage.getItem('text-size')) === null || JSON.parse(localStorage.getItem('text-size'))[apiUrl.split('?')[0]] === undefined ? 20 : JSON.parse(localStorage.getItem('text-size'))[apiUrl.split('?')[0]]);
  const fontSizeRef = useRef(fontSize);
  const [fontSizeSlideValue, setFontSizeSlideValue] = useState(fontSizeRef.current);
  const gridGap = 5;
  const pageButtonHeight = 30;
  const dataGridCellStyle = {
    backgroundColor: gridCellBackgroundColor,
    borderRadius: 7,
    color: gridCellFontColor,
    textAlign: 'center', 
    fontSize: fontSize, 
    height: fontSizeRef.current * 2,
    paddingLeft: 10,
    paddingRight: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    
    overflow: longText ? 'visible' : 'hidden', //longText가 true일 경우 수평스크롤 허용 및 텍스트 생략하지 않음 
  };
  const textStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis', 
    whiteSpace: 'nowrap'
  };
  const columnStyle = {
    ...dataGridCellStyle,
    backgroundColor: backgroundSubColor,
    color: titleColor,
    fontWeight: 'bold', 
  };
  const pageButtonStyle = {
    width: 'auto', 
    minWidth: 30, 
    height: 30, 
    justifyContent : 'center', 
    alignItems : 'center', 
    textAlign: 'center', 
    backgroundColor: gridCellBackgroundColor, 
    color: gridCellFontColor,
    border: 'none', 
    borderRadius: 10, 
    cursor: 'pointer'
  };

  const [pagination, setPagination] = useState({ totalPage: 1, currentPage: 1, row: -1 });

  const paginationRef = useRef(pagination);
  const gridDivRef = useRef(null);
  const apiUrlRef = useRef(null);

  const [dataGridStyle, setDataGridStyle] = useState({
    display: 'grid',
    gap: gridGap,
    backgroundColor: backgroundMainColor,
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    gridTemplateColumns: `repeat(5, auto)`,
    alignContent: 'start',
    whiteSpace: 'nowrap',
    height: `calc(100% - ${mainHeight}px - ${pageButtonHeight}px - 20px)` //100vh - mainHeight - pageButtonHeight - scrollBarMargin
  });

  useEffect(() => {
    let intervalId;

    if (setInquiryFunction !== undefined) {
      setInquiryFunction(() => () => {
        if (paginationRef.current.currentPage === paginationRef.current.totalPage) {
          ChangeMonitoring();
          setPagination({ totalPage: 1, currentPage: 1, row: -1 });
        } else {
          Inquiry(paginationRef.current.currentPage + 1);
        }
      });
    } else {
      if (inquiryInterval !== undefined) {
        intervalId = setInterval(() => {
          Inquiry(paginationRef.current.currentPage === paginationRef.current.totalPage ? 1 : paginationRef.current.currentPage + 1);
        }, 1000 * inquiryInterval);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      setFontSize(null);
      setFontSizeSlideValue(null);
      setPagination(null);
      setDataGridStyle(null);
      setColumn(null);
      setData(null);
      setPageButton(null);
    };
  }, [inquiryInterval]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    apiUrlRef.current = apiUrl;

    const textSize = JSON.parse(localStorage.getItem('text-size')) === null || JSON.parse(localStorage.getItem('text-size'))[apiUrl.split('?')[0]] === undefined ? 20 : JSON.parse(localStorage.getItem('text-size'))[apiUrl.split('?')[0]];
    if (fontSizeRef.current !== textSize){
      setFontSize(textSize);
      setFontSizeSlideValue(textSize);
    } else if (inquiryCondition) {
      Inquiry(pagination.currentPage);
    }

    window.addEventListener('resize', Resize);
    
    return () => {
      window.removeEventListener('resize', Resize);
    };
  }, [apiUrl]);
  
  useEffect(() => {
    fontSizeRef.current = fontSize;
    MoveFirstPage();
  }, [fontSize]);

  function Resize() {
    const row = Math.floor((gridDivRef.current.offsetHeight - gridGap) / (fontSizeRef.current * 2 + gridGap));
    if (row !== pagination.row) {
      pagination.row = row;
      setPagination((previous) => {
        return { ...previous, row: row }
      })
      Inquiry(pagination.currentPage);
    }
  }

  async function Inquiry(page) {
    if (apiUrlRef.current === '' || fontSizeRef.current === undefined) {
      return;
    }

    const row = gridDivRef.current ? Math.floor((gridDivRef.current.offsetHeight - gridGap) / (fontSizeRef.current * 2 + gridGap)) - 1 : 1;
    const response = await ApiGet(apiUrlRef.current + `row=${row}&page=${page}`);
    if (response !== null && response.ok) {
      const json = await response.json();

      const totalPage = json.count === 0 ? 1 : Math.ceil(json.count / row);

      setPagination(() => {
        return { currentPage: page, totalPage: totalPage, row: row };
      });

      const tenDigit = Math.floor((page - 1) / 10) * 10;
      setPageButton(
        new Array(10).fill(null).map((data, index) => (
          tenDigit + index + 1 <= totalPage && <button key={'page'+index} style={{ ...pageButtonStyle, backgroundColor: tenDigit + index + 1 === page ? 'gray' : gridCellBackgroundColor, color: gridCellFontColor }} onClick={() => { Inquiry(tenDigit + index + 1); }}>{tenDigit + index + 1}</button>
      )));
      
      const columns = Object.keys(json.data[0] || {});
      
      setDataGridStyle((previous => {
        return { ...previous, gridTemplateColumns: `repeat(${columns.length}, auto)` };
      }));

      setColumn(columns.map((data, index) => (
        <div key={'column'+index} style={{ ...columnStyle, fontSize: fontSizeRef.current, height: fontSizeRef.current * 2 }}>
          <span style={textStyle}>{data}</span>
        </div>
      )));

      const dataArray = [];
      for (const data of json.data) {
        for (const key in data) {
          if (isNaN(Number(data[key])) || data[key] === '' || data[key] === null || notNumberColumnArray.includes(key)) {
            dataArray.push(data[key]);
          } else {
            dataArray.push(Number(data[key]).toLocaleString());
          }
        }
      }
      
      if (rowSpanColumnArray.length > 0) {
        const invisibleDataIndexArray = [];
        const rowSpanDictionary = {};
        for (let i=0; i<dataArray.length; i++) {          
          if (dataArray[i] !== '' && dataArray[i] !== null) {
            FindSameValue(i, i);
          }
        }
        function FindSameValue(index, parentIndex) {
          if (!rowSpanColumnArray.includes(columns[index % columns.length])) {
            return;
          }

          const nextDataIndex = index + columns.length;
          if (nextDataIndex < dataArray.length && !invisibleDataIndexArray.includes(nextDataIndex)) {
            if (dataArray[index] === dataArray[nextDataIndex]) {
              if (rowSpanDictionary[parentIndex] === undefined) {
                rowSpanDictionary[parentIndex] = 2;
              } else {
                rowSpanDictionary[parentIndex] += 1;
              }

              invisibleDataIndexArray.push(nextDataIndex);
              FindSameValue(nextDataIndex, parentIndex);
            }
          }
        }
        setData(dataArray.map((data, index) => (
          !invisibleDataIndexArray.includes(index) && <div key={'data'+index} style={{ ...dataGridCellStyle, fontSize: fontSizeRef.current, height: rowSpanDictionary[index] ? fontSizeRef.current * 2 * rowSpanDictionary[index] + ((rowSpanDictionary[index] - 1) * gridGap) : fontSizeRef.current * 2, gridRow: rowSpanDictionary[index] ? `span ${rowSpanDictionary[index]}` : `span 1` }}>
            <span style={textStyle}>{data}</span>
          </div>
        )));
      } else {
        setData(dataArray.map((data, index) => (
          <div key={'data'+index} style={{ ...dataGridCellStyle, fontSize: fontSizeRef.current, height: fontSizeRef.current * 2 }}>
            <span style={textStyle}>{data}</span>
          </div>
        )));
      }
    }
  }

  const [column, setColumn] = useState(<></>);

  const [data, setData] = useState(<></>);

  const [pageButton, setPageButton] = useState(<></>);

  function MoveFirstPage() {
    Inquiry(1);
  }
  function MovePreviousPage() {
    const tenDigit = Math.floor((pagination.currentPage - 1) / 10) * 10;
    Inquiry(tenDigit === 0 ? pagination.totalPage : tenDigit - 9);
  }
  function MoveNextPage() {
    const tenDigit = Math.floor((pagination.currentPage - 1) / 10) * 10;
    if (pagination.currentPage === pagination.totalPage) {
      Inquiry(1);
    } else if (tenDigit + 11 > pagination.totalPage) {
      Inquiry(pagination.totalPage);
    } else {
      Inquiry(tenDigit + 11);
    }
  }
  function MoveLastPage() {
    Inquiry(pagination.totalPage);
  }

  function ChangeTextSize(event) {
    const textSize = Number(event.target.value);
    setFontSize(textSize);
    let storage = JSON.parse(localStorage.getItem('text-size'));
    if (storage === null) {
      storage = {};
    }
    storage[apiUrl.split('?')[0]] = textSize;
    localStorage.setItem('text-size', JSON.stringify(storage, null, 2));
  }
  function OnChangeSlideValue(event) {
    setFontSizeSlideValue(Number(event.target.value));
  }

  return (
    <div onContextMenu={(event)=>{ event.preventDefault(); }} style={{ userSelect: 'none', width: '100%', height: '100%', backgroundColor: backgroundMainColor }}>
      <div ref={gridDivRef} style={dataGridStyle}>
        {column}
        {data}
      </div>

      {apiUrl !== '' && <div style={{ height: pageButtonHeight, display: 'flex', justifyContent: 'space-between', backgroundColor: backgroundMainColor, padding: 20 }}>
        <div style={{ width: 180.25, height: 30 }}/>

        <div style={{ display: 'flex', gap: 5 }}>
          <button style={{ ...pageButtonStyle, lineHeight: 2.3 }} onClick={MoveFirstPage}>{pagination.totalPage !== 0 && 1} ◀◀</button>
          <button style={{ ...pageButtonStyle, lineHeight: 2.3 }} onClick={MovePreviousPage}>◀</button>
          {pageButton}
          <button style={{ ...pageButtonStyle, lineHeight: 2.3 }} onClick={MoveNextPage}>▶</button>
          <button style={{ ...pageButtonStyle, lineHeight: 2.3 }} onClick={MoveLastPage}>▶▶ {pagination.totalPage !== 0 && pagination.totalPage}</button>

        </div>

        <div style={{ display: 'flex', gap: 5 }}>
          <TextSize width='20' height='20' fill={'white'} style={{ marginTop: 4 }} />
          <input type='range' value={fontSizeSlideValue} min={10} max={50} step={5} onTouchEnd={ChangeTextSize} onMouseUp={ChangeTextSize} onChange={OnChangeSlideValue} style={{ accentColor: 'white' }}/>
          <label style={{ color: 'white', lineHeight: 1.7 }}>{fontSizeSlideValue}</label>
        </div>
      </div>}
    </div>
  );
}

export default PaginationGrid;